import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "../config";
import "../css/QuestionGenerator.css";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  FaMagic,
  FaRedo,
  FaTrash,
  FaCheck,
  FaStar,
  FaLightbulb,
  FaListUl,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { BlockMath, InlineMath } from "react-katex";

const romanNumerals = ["i)", "ii)", "iii)", "iv)", "v)"];

const CreateTheoryQuestion = ({ subjectId, subjectName, setInstTheories }) => {
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [numQuestions, setNumQuestions] = useState(1);
  const [subCount, setSubCount] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false); // For generation
  const [saving, setSaving] = useState(false); // For saving

  // ✅ Use your preferred token parse + headers method
  const tokenString = localStorage.getItem("authToken");
  let headers = { "Content-Type": "application/json" };
  if (tokenString) {
    try {
      const token = JSON.parse(tokenString);
      headers.Authorization = `Bearer ${token.access}`;
    } catch (e) {
      console.warn("Failed to parse authToken:", e);
    }
  }

  const generateQuestion = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/generate-theory/`,
        {
          subject: subjectName,
          topic,
          difficulty,
          num_questions: numQuestions,
          sub_count: numQuestions === 1 ? subCount : "",
        },
        { headers },
      );

      let generatedArray = [];

      if (Array.isArray(res.data.generated)) {
        generatedArray = res.data.generated.map((item) => ({
          question: item.question || "",
          answer: item.answer || "",
          mark: "",
          subQuestions: [],
        }));
      } else {
        const text = res.data.generated || "";
        const blocks = text
          .split(/(?:\*\*Q\d+:\*\*|Q\d+:)/i)
          .map((b) => b.trim())
          .filter(Boolean);

        generatedArray = blocks.map((block) => {
          let [qPart, aPart] = block.split(/(?:\*\*A\d+:\*\*|Answer:)/i);
          return {
            question: (qPart || "").trim(),
            answer: (aPart || "").trim(),
            mark: "",
            subQuestions: [],
          };
        });
      }

      // Add sub-questions if needed
      if (numQuestions === 1 && subCount) {
        const count = romanNumerals.indexOf(subCount) + 1;
        if (count > 0) {
          generatedArray[0].subQuestions = Array.from(
            { length: count },
            (_, idx) => ({
              label: romanNumerals[idx],
              text: "",
            }),
          );
        }
      }

      setResults(generatedArray);
    } catch (error) {
      console.error(error);
      toast.error("Error generating question.");
    }
    setLoading(false);
  };

  const updateMark = (index, value) => {
    const updated = [...results];
    updated[index].mark = value;
    setResults(updated);
  };

  const removeQuestion = (index) => {
    setResults((prev) => prev.filter((_, i) => i !== index));
  };

  const saveQuestion = async () => {
    setSaving(true);
    try {
      const savedQuestions = [];

      for (const item of results) {
        const payload = {
          Question: item.question,
          answer: item.answer,
          marks: item.mark,
          question_image: null,
          answer_image: null,
        };

        // Save to backend
        const { data } = await axios.post(
          `${API_URL}/instAItheory/${subjectId}/`,
          payload,
          { headers },
        );

        savedQuestions.push(data); // store the returned object
      }

      // ✅ Append newly saved questions to instTheories
      setInstTheories((prev) => [...savedQuestions, ...prev]);

      toast.success("Theory questions saved successfully!");
      setResults([]); // clear input blocks
    } catch (error) {
      console.error(error);
      toast.error("Error saving theory questions.");
    } finally {
      setSaving(false);
    }
  };

  const renderContent = (text) => {
    if (!text) return null;

    const trimmed = text.trim();

    // Detect $$ block math $$
    if (trimmed.startsWith("$$") && trimmed.endsWith("$$")) {
      const math = trimmed.replace(/^\$\$|\$\$$/g, "");
      return <BlockMath math={math} />;
    }

    // Detect $ inline math $
    if (trimmed.includes("$")) {
      const parts = trimmed.split(/(\$[^$]+\$)/g);

      return (
        <>
          {parts.map((part, index) => {
            if (part.startsWith("$") && part.endsWith("$")) {
              return (
                <InlineMath key={index} math={part.replace(/^\$|\$$/g, "")} />
              );
            } else {
              return <span key={index}>{part}</span>;
            }
          })}
        </>
      );
    }

    // Detect raw LaTeX (no $ included)
    const hasMathSymbols = /\\frac|\\sqrt|\\log|\\sin|\\cos|\\tan|\^|_/.test(
      trimmed,
    );
    if (hasMathSymbols) {
      return <InlineMath math={trimmed} />;
    }

    // Default: plain text
    return <span>{text}</span>;
  };

  const isSaveDisabled =
    results.length === 0 ||
    results.some((r) => !r.mark || Number(r.mark) <= 0) ||
    saving;

 return (
   <Box>
     {/* ── Generate Form ───────────────────────────────────────────────────── */}
     <Paper
       elevation={0}
       sx={{
         p: 2,
         mb: 2.5,
         borderRadius: 2,
         background: "rgba(124,58,237,0.04)",
         border: "1px solid rgba(124,58,237,0.14)",
       }}
     >
       <Typography
         sx={{
           fontSize: "0.68rem",
           fontWeight: 700,
           color: "#7c3aed",
           textTransform: "uppercase",
           letterSpacing: "0.8px",
           mb: 2,
         }}
       >
         🤖 AI Theory Generator
       </Typography>

       <Stack spacing={1.5}>
         {/* Topic */}
         <TextField
           value={topic}
           onChange={(e) => setTopic(e.target.value)}
           placeholder="Enter topic e.g. Newton's Laws"
           size="small"
           fullWidth
           InputProps={{
             startAdornment: (
               <InputAdornment position="start">
                 <FaLightbulb size={13} color="#7c3aed" />
               </InputAdornment>
             ),
           }}
           sx={{
             "& .MuiOutlinedInput-root": {
               color: "#0c1a2e",
               borderRadius: 2,
               background: "#fff",
               "& fieldset": { borderColor: "rgba(124,58,237,0.25)" },
               "&:hover fieldset": { borderColor: "#7c3aed" },
               "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
             },
             "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
           }}
         />

         {/* Difficulty */}
         <Select
           value={difficulty}
           onChange={(e) => setDifficulty(e.target.value)}
           displayEmpty
           size="small"
           fullWidth
           sx={{
             color: "#0c1a2e",
             borderRadius: 2,
             background: "#fff",
             "& .MuiOutlinedInput-notchedOutline": {
               borderColor: "rgba(124,58,237,0.25)",
             },
             "&:hover .MuiOutlinedInput-notchedOutline": {
               borderColor: "#7c3aed",
             },
             "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
               borderColor: "#7c3aed",
             },
             "& .MuiSvgIcon-root": { color: "#7c3aed" },
           }}
         >
           <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
             Select difficulty
           </MenuItem>
           {[
             { value: "easy", label: "Easy", color: "#059669" },
             { value: "medium", label: "Medium", color: "#d97706" },
             { value: "difficult", label: "Difficult", color: "#ef4444" },
           ].map((opt) => (
             <MenuItem key={opt.value} value={opt.value}>
               <Stack direction="row" alignItems="center" spacing={1}>
                 <Box
                   sx={{
                     width: 8,
                     height: 8,
                     borderRadius: "50%",
                     background: opt.color,
                   }}
                 />
                 <span>{opt.label}</span>
               </Stack>
             </MenuItem>
           ))}
         </Select>

         {/* Number of questions */}
         <TextField
           type="number"
           placeholder="Number of questions"
           value={numQuestions === 0 ? "" : numQuestions}
           onChange={(e) => {
             const val = e.target.value.replace(/\D/g, "");
             if (val === "") {
               setNumQuestions(0);
             } else {
               setNumQuestions(Math.max(1, Number(val)));
             }
             setSubCount("");
           }}
           onBlur={() => {
             if (numQuestions === 0) setNumQuestions(1);
           }}
           inputProps={{ min: 1 }}
           size="small"
           fullWidth
           InputProps={{
             startAdornment: (
               <InputAdornment position="start">
                 <FaListUl size={13} color="#7c3aed" />
               </InputAdornment>
             ),
           }}
           sx={{
             "& .MuiOutlinedInput-root": {
               color: "#0c1a2e",
               borderRadius: 2,
               background: "#fff",
               "& fieldset": { borderColor: "rgba(124,58,237,0.25)" },
               "&:hover fieldset": { borderColor: "#7c3aed" },
               "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
             },
             "& input::placeholder": { color: "#94a3b8", fontSize: "0.85rem" },
           }}
         />

         {/* Sub-question selector — only when numQuestions === 1 */}
         {numQuestions === 1 && (
           <Box>
             <Typography
               sx={{
                 fontSize: "0.68rem",
                 fontWeight: 700,
                 color: "#64748b",
                 textTransform: "uppercase",
                 letterSpacing: "0.6px",
                 mb: 0.75,
               }}
             >
               Sub-questions (optional)
             </Typography>
             <Select
               value={subCount}
               onChange={(e) => setSubCount(e.target.value)}
               displayEmpty
               size="small"
               fullWidth
               renderValue={(selected) =>
                 selected === "" ? (
                   <Typography sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
                     Select number of sub-questions
                   </Typography>
                 ) : (
                   selected.toUpperCase()
                 )
               }
               sx={{
                 color: "#0c1a2e",
                 borderRadius: 2,
                 background: "#fff",
                 "& .MuiOutlinedInput-notchedOutline": {
                   borderColor: "rgba(124,58,237,0.25)",
                 },
                 "&:hover .MuiOutlinedInput-notchedOutline": {
                   borderColor: "#7c3aed",
                 },
                 "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                   borderColor: "#7c3aed",
                 },
                 "& .MuiSvgIcon-root": { color: "#7c3aed" },
               }}
             >
               <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
                 Select number of sub-questions
               </MenuItem>
               {romanNumerals.map((roman) => (
                 <MenuItem key={roman} value={roman}>
                   {roman.toUpperCase()}
                 </MenuItem>
               ))}
             </Select>
           </Box>
         )}

         {/* Buttons */}
         <Stack direction="row" spacing={3} justifyContent={"space-between"}>
           <Button
             onClick={generateQuestion}
             disabled={loading}
             variant="contained"
            //  fullWidth
             startIcon={loading ? null : <FaMagic size={12} />}
             sx={{
               borderRadius: 2,
               textTransform: "none",
               fontWeight: 700,
               fontSize: "0.82rem",
               background: loading
                 ? "#e2e8f0"
                 : "linear-gradient(135deg, #7c3aed, #a855f7)",
               color: loading ? "#94a3b8" : "#fff",
               boxShadow: loading ? "none" : "0 4px 12px rgba(124,58,237,0.25)",
               "&:hover": {
                 background: loading
                   ? "#e2e8f0"
                   : "linear-gradient(135deg, #6d28d9, #7c3aed)",
               },
               "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
             }}
           >
             {loading ? (
               <Stack direction="row" alignItems="center" spacing={1}>
                 <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
                 <span>Generating…</span>
               </Stack>
             ) : (
               "Generate"
             )}
           </Button>

           <Button
             onClick={generateQuestion}
             disabled={loading}
             variant="outlined"
             startIcon={<FaRedo size={11} />}
             sx={{
               borderRadius: 2,
               textTransform: "none",
               fontWeight: 700,
               fontSize: "0.82rem",
               px: 2,
               borderColor: "rgba(124,58,237,0.3)",
               color: "#7c3aed",
               "&:hover": {
                 borderColor: "#7c3aed",
                 background: "rgba(124,58,237,0.06)",
               },
             }}
           >
             Retry
           </Button>
         </Stack>
       </Stack>
     </Paper>

     {/* ── Results ─────────────────────────────────────────────────────────── */}
     {results.length > 0 && (
       <Box>
         <Typography
           sx={{
             fontSize: "0.68rem",
             fontWeight: 700,
             color: "#64748b",
             textTransform: "uppercase",
             letterSpacing: "0.8px",
             mb: 1.5,
           }}
         >
           Generated Questions ({results.length})
         </Typography>

         <Stack spacing={1.5} mb={2}>
           {results.map((item, index) => (
             <Paper
               key={index}
               elevation={0}
               sx={{
                 borderRadius: 2,
                 background: "#f8fafc",
                 border: "1px solid #e2e8f0",
                 overflow: "hidden",
                 transition: "all 0.2s",
                 "&:hover": {
                   borderColor: "rgba(124,58,237,0.3)",
                   boxShadow: "0 2px 8px rgba(124,58,237,0.07)",
                 },
               }}
             >
               {/* Card header */}
               <Stack
                 direction="row"
                 alignItems="center"
                 justifyContent="space-between"
                 sx={{
                   px: 2,
                   py: 1,
                   borderBottom: "1px solid #e2e8f0",
                   background: "#fff",
                 }}
               >
                 <Stack direction="row" alignItems="center" spacing={1}>
                   <Box
                     sx={{
                       width: 24,
                       height: 24,
                       borderRadius: 1,
                       background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                       flexShrink: 0,
                     }}
                   >
                     <Typography
                       sx={{
                         fontSize: "0.65rem",
                         fontWeight: 800,
                         color: "#fff",
                       }}
                     >
                       Q{index + 1}
                     </Typography>
                   </Box>
                   <Typography
                     sx={{
                       fontSize: "0.72rem",
                       fontWeight: 700,
                       color: "#64748b",
                     }}
                   >
                     Question {index + 1}
                   </Typography>
                 </Stack>

                 <Tooltip title="Remove question" arrow>
                   <IconButton
                     size="small"
                     onClick={() => removeQuestion(index)}
                     sx={{
                       color: "#ef4444",
                       background: "rgba(239,68,68,0.08)",
                       border: "1px solid rgba(239,68,68,0.2)",
                       borderRadius: 1.5,
                       "&:hover": { background: "rgba(239,68,68,0.16)" },
                     }}
                   >
                     <FaTrash size={12} />
                   </IconButton>
                 </Tooltip>
               </Stack>

               {/* Question body */}
               <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#0288D1",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                     mb: 0.75,
                   }}
                 >
                   Question
                 </Typography>
                 <Box
                   sx={{
                     fontSize: "0.88rem",
                     color: "#0c1a2e",
                     lineHeight: 1.7,
                     wordBreak: "break-word",
                     whiteSpace: "pre-wrap",
                   }}
                 >
                   {renderContent(item.question)}
                 </Box>
               </Box>

               <Divider sx={{ borderColor: "#f1f5f9", mx: 2 }} />

               {/* Answer body */}
               <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#059669",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                     mb: 0.75,
                   }}
                 >
                   Answer
                 </Typography>
                 <Box
                   sx={{
                     fontSize: "0.88rem",
                     color: "#334155",
                     lineHeight: 1.7,
                     wordBreak: "break-word",
                     whiteSpace: "pre-wrap",
                   }}
                 >
                   {renderContent(item.answer)}
                 </Box>
               </Box>

               {/* Mark input */}
               <Box
                 sx={{
                   px: 2,
                   py: 1.2,
                   borderTop: "1px solid #e2e8f0",
                   background: "#fff",
                 }}
               >
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                   <Typography
                     sx={{
                       fontSize: "0.72rem",
                       fontWeight: 700,
                       color: "#64748b",
                       textTransform: "uppercase",
                       letterSpacing: "0.5px",
                       whiteSpace: "nowrap",
                     }}
                   >
                     Mark:
                   </Typography>
                   <TextField
                     type="number"
                     placeholder="e.g. 5"
                     value={item.mark}
                     onChange={(e) => updateMark(index, e.target.value)}
                     required
                     size="small"
                     inputProps={{ min: 0 }}
                     InputProps={{
                       startAdornment: (
                         <InputAdornment position="start">
                           <FaStar size={11} color="#7c3aed" />
                         </InputAdornment>
                       ),
                     }}
                     sx={{
                       width: 120,
                       "& .MuiOutlinedInput-root": {
                         color: "#0c1a2e",
                         borderRadius: 2,
                         background: "#f8fafc",
                         "& fieldset": { borderColor: "rgba(124,58,237,0.2)" },
                         "&:hover fieldset": { borderColor: "#7c3aed" },
                         "&.Mui-focused fieldset": { borderColor: "#7c3aed" },
                       },
                       "& input::placeholder": {
                         color: "#94a3b8",
                         fontSize: "0.82rem",
                       },
                     }}
                   />
                 </Stack>
               </Box>
             </Paper>
           ))}
         </Stack>

         {/* Save button */}
         <Button
           onClick={saveQuestion}
           disabled={isSaveDisabled}
           variant="contained"
           fullWidth
           startIcon={isSaveDisabled ? null : <FaCheck size={12} />}
           sx={{
             borderRadius: 2,
             textTransform: "none",
             fontWeight: 700,
             fontSize: "0.88rem",
             py: 1.2,
             background: isSaveDisabled
               ? "#e2e8f0"
               : "linear-gradient(135deg, #059669, #10b981)",
             color: isSaveDisabled ? "#94a3b8" : "#fff",
             boxShadow: isSaveDisabled
               ? "none"
               : "0 4px 12px rgba(5,150,105,0.25)",
             "&:hover": {
               background: isSaveDisabled
                 ? "#e2e8f0"
                 : "linear-gradient(135deg, #047857, #059669)",
             },
             "&:disabled": { color: "#94a3b8", background: "#e2e8f0" },
           }}
         >
           {saving ? (
             <Stack direction="row" alignItems="center" spacing={1}>
               <CircularProgress size={16} sx={{ color: "#94a3b8" }} />
               <span>Saving…</span>
             </Stack>
           ) : (
             `Save All ${results.length} Questions`
           )}
         </Button>
       </Box>
     )}
   </Box>
 );
};

export default CreateTheoryQuestion;
